import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { transactionApi } from "../services/api";
import { convertApiTransaction } from "../utils/helpers";
import { Transaction } from "../types";

interface MonthContextType {
  selectedMonth: number;
  setSelectedMonth: (month: number) => void;
  currentYear: number;
  setCurrentYear: (year: number) => void;
  transactions: Transaction[];
  loading: boolean;
  error: string | null;
  addTransaction: (transaction: Transaction) => void;
  updateTransaction: (transaction: Transaction) => void;
  deleteTransaction: (transactionId: string) => void;
  refreshTransactions: () => Promise<void>;
}

const MonthContext = createContext<MonthContextType | undefined>(undefined);

interface MonthProviderProps {
  children: ReactNode;
}

export const MonthProvider: React.FC<MonthProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false);

  const loadTransactions = useCallback(async () => {
    if (!user?.group_id || loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const monthStr = `${currentYear}-${String(selectedMonth + 1).padStart(
        2,
        "0"
      )}`;

      // 获取第一页数据以了解总页数
      const firstPageData = await transactionApi.getMonthlyTransactions(
        user.group_id,
        monthStr,
        1, // page
        100 // limit - API最大支持100
      );

      let allTransactions = [...firstPageData.transactions];

      // 如果有多页数据，继续获取剩余页面
      if (firstPageData.pagination && firstPageData.pagination.has_next) {
        const totalPages = firstPageData.pagination.total_pages;
        const remainingPages = [];

        // 创建剩余页面的请求
        for (let page = 2; page <= totalPages; page++) {
          remainingPages.push(
            transactionApi.getMonthlyTransactions(
              user.group_id,
              monthStr,
              page,
              100
            )
          );
        }

        // 并行获取所有剩余页面
        const remainingData = await Promise.all(remainingPages);

        // 合并所有页面的交易数据
        remainingData.forEach((pageData) => {
          allTransactions.push(...pageData.transactions);
        });
      }

      const convertedTransactions = allTransactions.map(convertApiTransaction);
      setTransactions(convertedTransactions);
    } catch (err: any) {
      setError(err.message || "Failed to load transactions");
      setTransactions([]);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [selectedMonth, currentYear, user?.group_id]);

  const addTransaction = useCallback((transaction: Transaction) => {
    setTransactions((prev) => [transaction, ...prev]);
  }, []);

  const updateTransaction = useCallback((updatedTransaction: Transaction) => {
    setTransactions((prev) =>
      prev.map((transaction) =>
        transaction.id === updatedTransaction.id
          ? updatedTransaction
          : transaction
      )
    );
  }, []);

  const deleteTransaction = useCallback((transactionId: string) => {
    setTransactions((prev) =>
      prev.filter((transaction) => transaction.id !== transactionId)
    );
  }, []);

  useEffect(() => {
    if (user?.group_id) {
      loadTransactions();
    } else {
      setTransactions([]);
      setError(null);
    }
  }, [user?.group_id, selectedMonth, loadTransactions]);

  return (
    <MonthContext.Provider
      value={{
        selectedMonth,
        setSelectedMonth,
        currentYear,
        setCurrentYear,
        transactions,
        loading,
        error,
        addTransaction,
        updateTransaction,
        deleteTransaction,
        refreshTransactions: loadTransactions,
      }}
    >
      {children}
    </MonthContext.Provider>
  );
};

export const useMonth = (): MonthContextType => {
  const context = useContext(MonthContext);
  if (context === undefined) {
    throw new Error("useMonth must be used within a MonthProvider");
  }
  return context;
};
